import { Box, Flex, Text } from '@mantine/core';
import { Fragment, useMemo } from 'react';
import {
  BORDER_COLOR,
  CELL_PADDING,
  FONT_SIZE,
  HEADER_BG,
  INDENT_SIZE,
  LEFT_SECTION_WIDTH,
  LINE_HEIGHT,
  TEXT_COLOR
} from '../tableConstants';
import { useCellNavigation } from '../useCellNavigation';
import { SingleFundHeaderRow } from '../components/SingleFundHeaderRow';
import { MultiFundRow } from '../components/MultiFundRow';
import { SingleFundRow } from '../components/SingleFundRow';
import { SeeMoreText } from '../components/SeeMoreText';
import { QuestionHeader } from '../components/QuestionHeader';
import { parseQuestionDisplayName } from '../tableUtils';

export const TurnoverTable = ({
  schema,
  fundId,
  isMultiFunds,
  funds,
  tableName,
  onChangeFund,
  questionLevels = [],
  groupColumn,
  rankLevels = [],
  name,
  isQuestionOpen = true,
  onToggleQuestion
}) => {
  const fundKeys = useMemo(() => Object.keys(funds || {}), [funds]);

  const { cellRefs, registerCell, onCellKeyDown } = useCellNavigation({ isMultiFunds, fundKeys });

  const rowDataByFund = useMemo(() => {
    return fundKeys.reduce((acc, key) => {
      const rows = funds?.[key]?.fund_data?.[tableName] || [];
      const rowByGroup = new Map();
      const rowIndexByGroup = new Map();

      rows.forEach((row, index) => {
        rowByGroup.set(row?.[groupColumn], row);
        rowIndexByGroup.set(row?.[groupColumn], index);
      });

      acc[key] = { rowByGroup, rowIndexByGroup };
      return acc;
    }, {});
  }, [fundKeys, funds, tableName, groupColumn]);

  const onCellChange = (column, value, _fundId, groupColumnValue) => {
    const targetFundId = _fundId || fundId;
    const currentTable = funds?.[targetFundId]?.fund_data?.[tableName] || [];
    const rowIndex = rowDataByFund?.[targetFundId]?.rowIndexByGroup?.get(groupColumnValue) ?? -1;
    const updatedTable = currentTable.map((row, index) =>
      index === rowIndex ? { ...row, [column]: value } : row
    );
    onChangeFund(tableName, updatedTable, _fundId);
  };

  const { questionNumber, questionName } = parseQuestionDisplayName(name);

  if (isMultiFunds) {
    const isWidthFit =
      fundKeys.length > 3 && window.innerWidth < fundKeys.length * 260 + LEFT_SECTION_WIDTH;

    return (
      <Flex
        direction="column"
        w={isWidthFit ? 'fit-content' : 'auto'}
        bd={`1px solid ${BORDER_COLOR}`}
      >
        <QuestionHeader
          fundKeys={fundKeys}
          funds={funds}
          name={questionName}
          number={questionNumber}
          isMultiFunds={isMultiFunds}
          isOpen={isQuestionOpen}
          onToggle={onToggleQuestion}
        />

        {isQuestionOpen &&
          schema.properties[groupColumn].rules?.array?.enum.map((item, index) => {
            return (
              <Flex direction="column" key={item.value}>
                {questionLevels.map((question, questionIndex) => {
                  const keyPrefix = question.level || question.name;

                  return (
                    <Fragment
                      key={`${tableName}-${index}-${item.value}-${questionIndex}-multi-view`}
                    >
                      {renderMultiFundsSimpleTable({
                        columns: question.columns,
                        children: question.children,
                        schema,
                        onCellChange,
                        levelIndex: 0,
                        funds,
                        fundKeys,
                        tableName,
                        level: question.level || rankLevels?.[index],
                        name: item.label,
                        cellRefs: { current: cellRefs, register: registerCell },
                        onCellKeyDown,
                        keyPrefix,
                        dataIndex: index,
                        groupColumnValue: item.value,
                        rowDataByFund
                      })}
                    </Fragment>
                  );
                })}
              </Flex>
            );
          })}
      </Flex>
    );
  }

  return (
    <Box bd={`1px solid ${BORDER_COLOR}`}>
      <QuestionHeader
        number={questionNumber}
        name={questionName}
        isMultiFunds={isMultiFunds}
        isOpen={isQuestionOpen}
        onToggle={onToggleQuestion}
      />
      {isQuestionOpen &&
        schema.properties[groupColumn].rules?.array?.enum.map((item, index) => {
          const row = rowDataByFund?.[fundId]?.rowByGroup?.get(item.value);

          return (
            <Flex direction="column" key={item.value}>
              {questionLevels.map((question, questionIndex) => {
                const keyPrefix = question.level ?? question.name;

                return (
                  <Fragment key={`${tableName}-${index}-${item.value}-${questionIndex}`}>
                    {renderSimpleTable({
                      columns: question.columns,
                      children: question.children,
                      schema,
                      columnValues: row || {},
                      onCellChange,
                      levelIndex: 0,
                      level: question.level || rankLevels?.[index],
                      name: item.label,
                      questionName: question.name,
                      dataIndex: index,
                      cellRefs: { current: cellRefs, register: registerCell },
                      onCellKeyDown,
                      keyPrefix,
                      fund: funds?.[fundId],
                      tableName,
                      groupColumnValue: item.value
                    })}
                  </Fragment>
                );
              })}
            </Flex>
          );
        })}
    </Box>
  );
};

export const renderMultiFundsSimpleTable = ({
  columns,
  schema,
  onCellChange,
  levelIndex,
  funds,
  fundKeys,
  tableName,
  level,
  name,
  children,
  cellRefs,
  onCellKeyDown,
  keyPrefix,
  groupColumnValue,
  rowDataByFund
}) => {
  const columnSize = Object.keys(columns).length;
  return (
    <>
      {columnSize > 1 && (
        <Flex
          id={`turnover-multi-strip-${groupColumnValue}`}
          sx={() => ({
            borderBottom: `1px solid ${BORDER_COLOR}`,
            borderRight: `1px solid ${BORDER_COLOR}`
          })}
        >
          <Flex
            id={`turnover-multi-strip-left-${groupColumnValue}`}
            sx={() => ({
              position: 'sticky',
              left: '0',
              zIndex: 1000
            })}
            maw={LEFT_SECTION_WIDTH}
            miw={LEFT_SECTION_WIDTH}
          >
            {levelIndex > 0 && (
              <Box
                maw={`${(levelIndex + 1) * INDENT_SIZE}px`}
                miw={`${(levelIndex + 1) * INDENT_SIZE}px`}
              ></Box>
            )}
            <Flex
              id={`turnover-multi-strip-inner-${groupColumnValue}`}
              flex="auto"
              sx={() => ({
                border: `1px solid ${BORDER_COLOR}`,
                borderTop: 'none',
                position: 'sticky'
              })}
            >
              {level && (
                <Text
                  id={`turnover-multi-strip-level-${groupColumnValue}`}
                  p={CELL_PADDING}
                  sx={() => ({
                    borderRight: `1px solid ${BORDER_COLOR}`
                  })}
                  bg={HEADER_BG}
                  miw={`${INDENT_SIZE}px`}
                  ta="center"
                  c={TEXT_COLOR}
                  fz={FONT_SIZE}
                  lh={LINE_HEIGHT}
                >
                  {level}
                </Text>
              )}

              <Text
                id={`turnover-multi-strip-name-${groupColumnValue}`}
                p={CELL_PADDING}
                bg={HEADER_BG}
                flex="auto"
                c={TEXT_COLOR}
                fz={FONT_SIZE}
                lh={LINE_HEIGHT}
              >
                <SeeMoreText text={name} />
              </Text>
            </Flex>
          </Flex>
          <Text
            id={`turnover-multi-strip-right-${groupColumnValue}`}
            p={CELL_PADDING}
            sx={() => ({
              borderRight: `1px solid ${BORDER_COLOR}`,
              borderBottom: `1px solid ${BORDER_COLOR}`
            })}
            bg={HEADER_BG}
            flex="auto"
          ></Text>
        </Flex>
      )}
      {!children &&
        Object.keys(columns).map(column => (
          <MultiFundRow
            key={column}
            columnSchema={columns[column]}
            cellSchema={schema.properties[column]}
            levelIndex={levelIndex}
            fundKeys={fundKeys}
            getValue={fundKey => rowDataByFund?.[fundKey]?.rowByGroup?.get(groupColumnValue) || {}}
            onFundChange={(fundKey, value) =>
              onCellChange(column, value, fundKey, groupColumnValue)
            }
            cellRefs={cellRefs}
            getCellKey={fundKey => `${keyPrefix}-${column}-${fundKey}`}
            onCellKeyDown={onCellKeyDown}
            label={columnSize === 1 ? name : undefined}
            columnLevel={columnSize === 1 ? level : undefined}
            funds={funds}
            tableName={tableName}
            fieldName={column}
            hasDrillDownFeature={schema.properties[column]?.has_drill_down_feature}
            symbol={schema.properties[column]?.symbol}
          />
        ))}

      {children &&
        children.map(question =>
          renderMultiFundsSimpleTable({
            columns: question.columns,
            children: question.children,
            schema,
            onCellChange,
            levelIndex: levelIndex + 1,
            funds,
            fundKeys,
            tableName,
            level: question.level,
            name: question.name,
            cellRefs,
            onCellKeyDown,
            keyPrefix: question.level ?? question.name
          })
        )}
    </>
  );
};

export const renderSimpleTable = ({
  columns,
  schema,
  columnValues,
  onCellChange,
  levelIndex,
  level,
  name,
  children,
  dataIndex,
  cellRefs,
  onCellKeyDown,
  keyPrefix,
  questionName,
  fund,
  tableName,
  groupColumnValue
}) => {
  const columnSize = Object.keys(columns).length;
  return (
    <>
      {dataIndex === 0 && (
        <SingleFundHeaderRow
          id={`turnover-single-header-${groupColumnValue}`}
          levelIndex={0}
          name={questionName}
        />
      )}
      {columnSize > 1 && (
        <SingleFundHeaderRow
          id={`turnover-single-header-detail-${groupColumnValue}-${dataIndex}`}
          levelIndex={questionName ? levelIndex + 1 : levelIndex}
          level={level}
          name={name}
        />
      )}

      {!children &&
        Object.keys(columns).map(column => (
          <SingleFundRow
            key={column}
            columnSchema={columns[column]}
            cellSchema={schema.properties[column]}
            levelIndex={level && columnSize > 1 ? levelIndex + 1 : levelIndex}
            value={columnValues[column]}
            error={columnValues?.errors?.[column]}
            onChange={value => onCellChange(column, value, null, groupColumnValue)}
            cellRefs={cellRefs}
            cellKey={`${keyPrefix}-${column}-${dataIndex}`}
            onCellKeyDown={onCellKeyDown}
            showIndent={Boolean(level) || questionName}
            label={columnSize === 1 ? name : undefined}
            columnLevel={columnSize === 1 ? level : undefined}
            fundId={fund?.fund_id}
            fundName={fund?.fund_name}
            tableName={tableName}
            fieldName={column}
            hasDrillDownFeature={schema.properties[column]?.has_drill_down_feature}
            symbol={schema.properties[column]?.symbol}
            tableId={columnValues['id']}
            reportId={columnValues['report_id']}
            isFromStaticTemplate={columnValues?.['static_template_fields']?.includes(column)}
          />
        ))}

      {children &&
        children.map(question => (
          <Fragment key={question.level}>
            {renderSimpleTable({
              columns: question.columns,
              children: question.children,
              schema,
              columnValues,
              onCellChange,
              levelIndex: questionName ? levelIndex + 2 : levelIndex + 1,
              level: question.level,
              name: question.name,
              cellRefs,
              onCellKeyDown,
              keyPrefix: question.level ?? question.name,
              fund,
              tableName
            })}
          </Fragment>
        ))}
    </>
  );
};
